import { Component, AfterViewInit, ViewChild, ElementRef, Input, OnInit, EventEmitter, Output, PLATFORM_ID, Inject } from '@angular/core';
import { Chart } from 'chart.js';
import { FinancialDataService } from 'src/app/features/stock-screener/services/financial-data.service';
import { StockFilterService } from 'src/app/features/stock-screener/services/stock-filter.service';
import { Hit } from 'src/app/models/sector-industries/sector-industries.model';
import { ChangeDetectorRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Component({
    selector: 'app-bar-chart',
    templateUrl: './bar-chart.component.html',
    styleUrls: ['./bar-chart.component.scss'],
    standalone: false
})
export class BarChartComponent implements OnInit, AfterViewInit {
  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;
  @Input() ticker: string = "";
  eps: any[] = [];
  chart!: Chart;
  private chartInitialized = false;
  quartPeriod: string[] = [];
  @Output() noDataEvent = new EventEmitter<boolean>();
  noData: boolean = false;
  isBrowser: boolean = false;
  chartLabels: string[] = [];

  constructor(
    private financialService: StockFilterService,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit() {
    this.getActualQuarterlyData("eps", "quarterly", this.eps);
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngAfterViewInit() {
    if (this.isBrowser) {
      this.initializeChart();
    }
  }

  private initializeChart() {
    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (ctx) {
      const datasetToShow = this.eps.length ? this.eps : [0, 0, 0, 0];

      // Use dynamic labels from component property
      const labels = this.chartLabels.length ? this.chartLabels : ['Q2 ‘24', 'Q3 ‘24', 'Q4 ‘24', 'Q1 ‘25'];

      const groupedData = {
        labels: labels,
        datasets: [
          {
            label: 'Earnings per Share',
            data: datasetToShow,
            backgroundColor: '#6F89E3',
            borderRadius: 10,
          }
        ]
      };

      this.chart = new Chart(ctx, {
        type: 'bar',
        data: groupedData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 30
            }
          },
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              align: 'start',
              labels: {
                usePointStyle: true,
                boxWidth: 8,
                padding: 40
              },
            },
            // @ts-ignore
            datalabels: {
              anchor: 'end',
              align: 'top',
              display: false,
              formatter: (value: number) => `${value}`,
              font: {
                size: 14,
                weight: 400,
                family: 'Poppins, sans-serif',
              },
              color: '#000'
            }
          },
          scales: {
            x: {
              display: true,
              grid: {
                display: false
              }
            },
            y: {
              display: true,
              position: 'right',
              ticks: {
                callback: (value: any) => `${value}`,
              },
              grid: {
                display: true,
                color: '#E7E7E7',
                lineWidth: 3,
                drawBorder: false
              }
            }
          }
        }
      });

      this.chartInitialized = true;
    }
  }


  private updateChart() {
    if (this.chart?.data?.datasets?.length) {
      this.chart.data.datasets[0].data = this.eps;
      this.chart.data.labels = this.chartLabels;
      this.chart.update();
    }
  }

  async getActualQuarterlyData(name: string, timePeriod: string, targetArray: any[]) {
    try {
      const ticker = this.ticker;
      const response = await this.financialService.getRatQuarterlyData(ticker, this.quartPeriod, name, timePeriod);

      targetArray.length = 0;
      this.chartLabels = [];

      if (response?.hits) {
        // Create an array to store all quarters with their dates and values
        const allQuarters: { date: Date; value: number; period: string }[] = [];

        // Process all hits and collect unique quarters
        response.hits
          .filter((hit: Hit) => hit.document.name === name)
          .forEach((hit: Hit) => {
            const periodDate = new Date(hit.document.period);
            const periodStr = hit.document.period;

            // Check if this quarter already exists in our collection
            const exists = allQuarters.some(q => q.period === periodStr);

            if (!exists) {
              allQuarters.push({
                date: periodDate,
                value: hit.document.v,
                period: periodStr
              });
            }
          });

        // Sort quarters by date (oldest first)
        allQuarters.sort((a, b) => a.date.getTime() - b.date.getTime());

        // Take the most recent 4 quarters
        const last4Quarters = allQuarters.slice(-4);

        // Prepare data for chart (oldest to newest)
        last4Quarters.forEach(quarter => {
          const month = quarter.date.getMonth() + 1;
          const year = quarter.date.getFullYear().toString().slice(-2);
          let quarterLabel = '';

          if (month >= 1 && month <= 3) quarterLabel = `Q1 '${year}`;
          else if (month >= 4 && month <= 6) quarterLabel = `Q2 '${year}`;
          else if (month >= 7 && month <= 9) quarterLabel = `Q3 '${year}`;
          else if (month >= 10 && month <= 12) quarterLabel = `Q4 '${year}`;

          targetArray.push(quarter.value); // Add to end to maintain chronological order
          this.chartLabels.push(quarterLabel);
        });

        this.noData = targetArray.filter(v => v !== null && v !== undefined).length === 0;
      } else {
        this.noData = true;
      }

      this.noDataEvent.emit(this.noData);
      this.cdr.detectChanges();

      if (this.chartInitialized) {
        this.updateChart();
      }
    } catch (error) {
      this.noData = true;
      this.noDataEvent.emit(this.noData);
      throw error;
    }
  }
}
